import React, {useCallback, useEffect, useState} from 'react';
import {
  useModelDetail,
  approveModelAccessRequest,
  isModelExternal,
  approveFileAccessRequest,
} from '../../../../store/model';
import {MRAppRoutes, useMatchedRoute} from '../../../router/routes';
import DefaultPage from '../../../layout/common/DefaultPage';
import styles from './ModelDetail.module.scss';
import {Flex, Spin} from 'antd';
import ModelOverview from '../common/ModelOverview/ModelOverview';
import ModelAccessConfirmationDialog from '../common/ModelRequestAccess/ModelAccessConfirmationDialog';
import {trackModelAccessGranted} from '../../../../tracking/tracking';
import {messageAdd, MessageTypes} from '../../../../store/message';
import {useDispatch} from 'react-redux';
import DialogProgress from '../../../core/dialog/DialogProgress';
import ModelOverviewTH from '../common/ModelOverview/ModelOverviewTH';
import {ModelFamilyDisplay} from './ModelFamilyDisplay';
import {useModelFamily} from './useModelFamily';
import {ModelDetailBreadcrumb} from './ModelDetailBreadcrumb';
import {ModelDetailTabs} from './ModelDetailTabs';
import {ModelDetailCard} from './ModelDetailCard';
import {canViewRequestChangeButton} from '../../../../permissions/usePermissions';
import {useCurrentUser} from '../../../../store/auth';
import {ModelDTO, UserType} from 'hemwb-api';

const ModelDetail: React.FC = () => {
  const dispatch = useDispatch();
  const {uuid} = useMatchedRoute(MRAppRoutes.MODEL_DETAIL);
  let model = useModelDetail(uuid);
  const familyData = useModelFamily(uuid);
  const familyModels = familyData?.familyTree;
  const parentModel = familyData?.parentModel;
  const queryParams = new URLSearchParams(window.location.hash.split('?')[1]);
  const role = queryParams.get('role');
  const tab = queryParams.get('tab');
  const fileUuid = queryParams.get('file');
  const fileName = queryParams.get('fileName');
  const requesterUserId = queryParams.get('userId');
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const [submitText, setSubmitText] = useState('');
  const externalModel = isModelExternal(model);
  const enableRequestChangeButton = canViewRequestChangeButton(model as ModelDTO);
  const currentUser = useCurrentUser();
  const isExternalUserFlag = currentUser?.userType === UserType.EXTERNALUSER;
  // HEMT-8938: only the original creator of the entry or a super user can turn AI summary on/off
  const isOwner =
    (!!currentUser?.id && model?.createdBy?.id === currentUser.id) || currentUser?.userType === UserType.SUPERUSER;
  // HEMT-8938: read aiSummaryOptedOut from the model detail DTO (backend sends it)
  // Cast is required until yarn api regenerates the hemwb-api types from the updated spec
  const aiSummaryOptedOut = !!(model as any)?.aiSummaryOptedOut;
  // HEMT-9520: read aiSummaryStatus from the model detail DTO (backend sends it)
  const aiSummaryStatus = (model as any)?.aiSummaryStatus;
  // HEMT-8938: model has MDP if the metadata field is non-empty — used to distinguish
  // 'summary generating' from 'no MDP' during the brief window after model creation
  const hasMdp = !!(model as any)?.metadata?.['model_development_plan'];
  // HEMT-9520: model has a Technical Report if backend populated hasTechnicalReport on the DTO
  const hasTechnicalReport = !!(model as any)?.hasTechnicalReport;
  const provideAccess = useCallback(() => {
    if (model && fileUuid && fileName && requesterUserId && !requested) {
      approveFileAccessRequest(model.uuid, fileUuid, requesterUserId, fileName)
        .then(() => {
          setSubmitText('');
          setOpen(true);
          setRequested(true);
        })
        .catch(() => {
          setSubmitText('');
          setRequested(true);
          dispatch(messageAdd('Failed to Grant File access', MessageTypes.ERROR));
        });
    }

    if (requesterUserId && role && model && !requested) {
      setSubmitText('Updating Access');
      approveModelAccessRequest(model.uuid, role, requesterUserId)
        .then(() => {
          trackModelAccessGranted(model);
          setSubmitText('');
          setOpen(true);
          setRequested(true);
        })
        .catch(() => {
          setSubmitText('');
          setRequested(true);
          dispatch(messageAdd('Failed to Grant Model access', MessageTypes.ERROR));
        });
    }
  }, [dispatch, requesterUserId, role, model, requested, fileUuid, fileName]);

  useEffect(() => {
    provideAccess();
  }, [provideAccess]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setRequested(true);
  }, []);

  return (
    <>
      <DefaultPage style={{overflow: 'hidden'}}>
        <div className={styles.modelDetail}>
          <Flex style={{backgroundColor: 'white', height: '49px', alignItems: 'center', padding: '0 0 0 24px'}}>
            <ModelDetailBreadcrumb currentModel={model} parentModel={parentModel} />
          </Flex>
          {!model ? (
            // Loading state
            <div className={styles.loadingContainer}>
              <Spin size="large" tip="Loading model details..." />
            </div>
          ) : (
            <>
              {/* New two-card layout */}
              <div className={styles.twoCardLayout}>
                {/* Left card - Details */}
                <div className={styles.detailsCard}>
                  <ModelDetailCard model={model} isExternalUser={isExternalUserFlag} />
                </div>

                {/* Right card - Tabs (flex, takes remaining space) */}
                <div className={styles.tabsCard}>
                  <ModelDetailTabs
                    model={model}
                    aiSummary={model.aiSummary}
                    enableRequestChange={enableRequestChangeButton}
                    currentModelUuid={uuid}
                    familyModels={familyModels}
                    aiSummaryOptedOut={aiSummaryOptedOut}
                    aiSummaryStatus={aiSummaryStatus}
                    isOwner={isOwner}
                    hasMdp={hasMdp}
                    hasTechnicalReport={hasTechnicalReport}
                  />
                </div>
              </div>

              {/* OLD IMPLEMENTATION - Keep for reference */}
              {false && (
                <div style={{marginTop: '24px', opacity: 0.5, pointerEvents: 'none'}}>
                  <h4 style={{padding: '0 24px'}}>OLD IMPLEMENTATION (for reference):</h4>
                  <div className={styles.content}>
                    <ModelFamilyDisplay
                      models={familyModels}
                      currentModelUuid={uuid}
                      loading={!familyModels}
                      isExternalUser={isExternalUserFlag}
                    />
                    <br />
                    {externalModel ? (
                      <ModelOverviewTH model={model} selectedTab={tab ? tab : 'text'} expanded={true} />
                    ) : (
                      <ModelOverview model={model} selectedTab={tab ? tab : 'text'} expanded={true} />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DefaultPage>
      <DialogProgress open={!!submitText} title="Setting up your model permissions" text={submitText} />
      {/* <ModelActionMenu model={model || undefined} /> */}

      {model && (
        <ModelAccessConfirmationDialog
          requesterUserId={requesterUserId}
          modelUuid={model?.uuid}
          role={role ? role : ''}
          fileName={fileName}
          open={open}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

export default ModelDetail;
